import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'

/**
 * Shared credit gate hook — single source of truth for credit checking.
 *
 * Returns current balance, a refresh function, and a guarded navigate
 * that checks credits before allowing navigation to analysis pages.
 */
/**
 * Anonymous-likely visitors have no account, so /entitlements/usage will
 * always 401. Firing it on every public page load adds a critical-path
 * round-trip + a console error (Lighthouse Best Practices warning) for
 * zero benefit. Match the auth-bootstrap gating: only call when there's
 * a token or a previously-cached user.
 */
function isProbablyAuthenticated(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false
    if (localStorage.getItem('fth_token')) return true
    if (localStorage.getItem('fth_user_cache')) return true
  } catch { /* private mode etc. — fall through */ }
  return false
}


export function useCreditGate(isPro: boolean) {
  const [balance, setBalance] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const navigate = useNavigate()

  const refresh = useCallback(async () => {
    if (isPro) {
      setBalance(-1) // Pro = unlimited display
      setLoading(false)
      return
    }
    if (!isProbablyAuthenticated()) {
      // Anonymous — don't make the call (always 401). Render as "no
      // credits" so anything that needs credits gates correctly.
      setBalance(0)
      setLoading(false)
      return
    }
    try {
      const usage = await api.getUsage()
      setBalance(usage.credits_balance)
    } catch {
      setBalance(0)
    } finally {
      setLoading(false)
    }
  }, [isPro])

  // Fetch on mount
  useEffect(() => {
    refresh()
  }, [refresh])

  // Refresh when tab becomes visible (user returns from pricing page, etc.)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [refresh])

  const hasCredits = isPro || (balance !== null && balance > 0)

  /** Navigate to path if credits available, show modal otherwise. */
  const navigateWithCreditCheck = useCallback(
    async (path: string) => {
      // Anonymous visitors have no balance to check. Let them through;
      // the backend will gate them properly at submit time. This also
      // avoids a guaranteed-401 round-trip on every gated link click
      // before login.
      if (!isPro && !isProbablyAuthenticated()) {
        navigate(path)
        return
      }
      try {
        const usage = await api.getUsage()
        setBalance(usage.credits_balance)
        if (isPro || usage.credits_balance > 0) {
          navigate(path)
        } else {
          setShowModal(true)
        }
      } catch {
        // On error, let them through — backend will gate at submit
        navigate(path)
      }
    },
    [isPro, navigate],
  )

  return {
    balance,
    hasCredits,
    loading,
    showModal,
    setShowModal,
    refresh,
    navigateWithCreditCheck,
  }
}
